/**
 * AiQuotaWidget – live-widget för AI-guard-status. Visar användning per timme/dygn
 * mot konfigurerat tak + trafikljus för snabb överblick. Polling var 30:e sekund.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, Activity, ShieldCheck, ShieldAlert, ShieldOff, Loader2 } from "lucide-react";
import api from "@/lib/api";

function ProgressBar({ used, cap, label, testid }) {
  const pct = Math.min(100, Math.round((used / Math.max(1, cap)) * 100));
  const color = pct >= 90 ? "bg-red-600" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-zinc-700">{label}</span>
        <span className="font-medium text-zinc-900 tabular-nums">
          {used.toLocaleString("sv-SE")} / {cap.toLocaleString("sv-SE")} ({pct}%)
        </span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AiQuotaWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get("/ai/guard/status");
        if (mounted) {
          setData(data);
          setError(null);
        }
      } catch (e) {
        if (mounted) setError(e?.response?.status === 403 ? "Endast admin" : "Kunde inte hämta status");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 30000);
    return () => {
      mounted = false;
      clearInterval(iv);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-6 flex items-center gap-3 text-zinc-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar AI-quota…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-6 text-sm text-zinc-500" data-testid="ai-quota-error">
        {error || "Ingen data tillgänglig"}
      </div>
    );
  }

  const { enabled, global_hour, global_day, inflight, limits } = data;
  const hourPct = Math.round((global_hour.used / Math.max(1, global_hour.cap)) * 100);
  const dayPct = Math.round((global_day.used / Math.max(1, global_day.cap)) * 100);
  const worst = Math.max(hourPct, dayPct);

  const StatusIcon = !enabled ? ShieldOff : worst >= 90 ? ShieldAlert : ShieldCheck;
  const statusColor = !enabled
    ? "text-zinc-500"
    : worst >= 90
    ? "text-red-600"
    : worst >= 70
    ? "text-amber-600"
    : "text-emerald-600";
  const statusLabel = !enabled
    ? "AVSTÄNGD"
    : worst >= 90
    ? "KRITISK"
    : worst >= 70
    ? "VARNING"
    : "OK";

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="ai-quota-widget">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-700" />
          <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>AI-quota (live)</h2>
        </div>
        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold ${statusColor}`}>
          <StatusIcon className="w-4 h-4 stroke-[2]" />
          <span data-testid="ai-quota-status">{statusLabel}</span>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-5">
        Skydd mot runaway LLM-kostnader. Räknare rensas löpande via TTL. Uppdateras var 30:e sekund.
      </p>

      <div className="space-y-4">
        <ProgressBar
          used={global_hour.used}
          cap={global_hour.cap}
          label="Anrop den senaste timmen"
          testid="ai-quota-hour"
        />
        <ProgressBar
          used={global_day.used}
          cap={global_day.cap}
          label="Anrop det senaste dygnet"
          testid="ai-quota-day"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-zinc-100 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
        <div>
          <div className="text-zinc-500 uppercase tracking-wider font-semibold">Aktiva anrop</div>
          <div className="mt-0.5 flex items-center gap-1 text-lg font-semibold text-zinc-900 tabular-nums">
            <Activity className="w-3.5 h-3.5 text-blue-700" />
            {inflight}
          </div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-wider font-semibold">IP/min</div>
          <div className="mt-0.5 text-lg font-semibold text-zinc-900 tabular-nums">{limits.ip_per_min}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-wider font-semibold">IP/timme</div>
          <div className="mt-0.5 text-lg font-semibold text-zinc-900 tabular-nums">{limits.ip_per_hour}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-wider font-semibold">Prompt max</div>
          <div className="mt-0.5 text-lg font-semibold text-zinc-900 tabular-nums">
            {limits.max_prompt_chars.toLocaleString("sv-SE")}
          </div>
        </div>
      </div>

      {worst >= 70 && enabled && (
        <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5 text-sm text-amber-900">
          <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            {worst >= 90
              ? "Kritiskt: minst en kvot är nästan slut. Nya AI-anrop börjar nekas snart."
              : "Varning: användningen närmar sig taket. Överväg att höja gränsen eller invänta återställning."}
          </div>
        </div>
      )}
    </div>
  );
}
